import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2, Shield, Copy, Check } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/hooks/use-toast';

interface MFAEnrollDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onEnrolled?: () => void;
}

export function MFAEnrollDialog({ open, onOpenChange, onEnrolled }: MFAEnrollDialogProps) {
  const [step, setStep] = useState<'loading' | 'scan' | 'verify'>('loading');
  const [qrCode, setQrCode] = useState('');
  const [secret, setSecret] = useState('');
  const [factorId, setFactorId] = useState('');
  const [verifyCode, setVerifyCode] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [copied, setCopied] = useState(false);

  const startEnrollment = async () => {
    try {
      setStep('loading');
      const { data, error } = await supabase.auth.mfa.enroll({
        factorType: 'totp',
        friendlyName: 'Authenticator App',
      });

      if (error) throw error;

      setQrCode(data.totp.qr_code);
      setSecret(data.totp.secret);
      setFactorId(data.id);
      setStep('scan');
    } catch (error: any) {
      console.error('MFA enroll error:', error);
      toast({ title: 'Failed to start 2FA setup', description: error.message, variant: 'destructive' });
      onOpenChange(false);
    }
  };

  const handleVerify = async () => {
    if (verifyCode.length !== 6) return;

    try {
      setVerifying(true);
      const { error } = await supabase.auth.mfa.challengeAndVerify({
        factorId,
        code: verifyCode,
      });

      if (error) throw error;

      toast({ title: '2FA Enabled', description: 'Two-factor authentication is now active on your account.' });
      onEnrolled?.();
      onOpenChange(false);
    } catch (error: any) {
      console.error('MFA verify error:', error);
      toast({ title: 'Verification failed', description: 'Invalid code. Please try again.', variant: 'destructive' });
      setVerifyCode('');
    } finally {
      setVerifying(false);
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(secret);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setStep('loading');
      setQrCode('');
      setSecret('');
      setFactorId('');
      setVerifyCode('');
    } else {
      startEnrollment();
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Set Up Two-Factor Authentication
          </DialogTitle>
          <DialogDescription>
            Use an authenticator app like Google Authenticator or Authy to scan the QR code below.
          </DialogDescription>
        </DialogHeader>

        {step === 'loading' && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}

        {step === 'scan' && (
          <div className="space-y-4">
            <div className="flex justify-center">
              <div className="rounded-lg border bg-white p-3">
                <img src={qrCode} alt="Scan this QR code with your authenticator app" className="h-48 w-48" />
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Can't scan? Enter this key manually:</Label>
              <div className="flex items-center gap-2">
                <code className="flex-1 rounded bg-muted px-3 py-2 text-xs font-mono break-all select-all">
                  {secret}
                </code>
                <Button variant="outline" size="icon" onClick={handleCopySecret} className="shrink-0">
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                </Button>
              </div>
            </div>

            <div className="space-y-2 pt-2">
              <Label htmlFor="mfa-code">Enter the 6-digit code from your app</Label>
              <Input
                id="mfa-code"
                value={verifyCode}
                onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="text-center text-lg tracking-widest font-mono"
                autoComplete="one-time-code"
              />
            </div>

            <Button
              className="w-full"
              onClick={handleVerify}
              disabled={verifyCode.length !== 6 || verifying}
            >
              {verifying && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Verify & Enable 2FA
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
